import { useState } from 'react';
import { LIFECYCLE_LABEL, LIFECYCLE_BADGE } from '../lifecycle.js';

/** The status, as one badge. Used everywhere a task is shown. */
export function LifecycleBadge({ status, title }) {
  return (
    <span className={`o-badge ${LIFECYCLE_BADGE[status] || 'o-grey'}`} title={title}>
      {LIFECYCLE_LABEL[status] || status}
    </span>
  );
}

/**
 * The moves this person can make on this task, as buttons.
 *
 * The options come from the server with every response, already narrowed to the caller's role —
 * so a developer never sees a Pass button to be refused for pressing. Rendering nothing is a
 * correct outcome: a task in QA, read by a developer, has no move that belongs to them.
 *
 * A backwards move needs a reason before it can be sent. The note field opens in place rather
 * than in a dialog: rejecting is a thing done while looking at the task, and a modal covers the
 * screenshot the rejection is usually about.
 */
export default function LifecycleControl({
  task, options, onTransition, saving, compact = false,
}) {
  const [rejecting, setRejecting] = useState(null);   // null | the status being moved to
  const [note, setNote] = useState('');

  const allowed = options?.transitions?.[task.lifecycle_status] || [];
  if (!allowed.length) return null;

  // A move back to in_progress out of either QA state is a rejection — the one kind that has to
  // say why. Mirrors the server's `rejection` flag; kept as one predicate so the button label,
  // the note requirement and the confirm all agree on what a rejection is.
  const isRejection = (to) => to === 'in_progress'
    && ['ready_for_test_qa', 'ready_for_prod_qa'].includes(task.lifecycle_status);

  // Verbs, not state names. A button says what pressing it does; the badge beside it already
  // says where the task is.
  //
  // `compact` is the in-a-table-row form, and the labels shorten because the row may not grow:
  // two buttons that wrap onto a second line are a taller row, and a table whose rows are
  // different heights depending on which buttons a task happens to offer does not scan. The
  // full wording is on the title attribute, where it costs nothing.
  const LONG = {
    'ready': 'Ready to start',
    'ready_for_test_qa': 'Ready for test QA',
    'ready_for_prod_qa': 'Ready for prod QA',
    'needs_decision': 'Needs decision',
  };
  const SHORT = {
    'ready': 'Ready',
    'ready_for_test_qa': 'To QA',
    'ready_for_prod_qa': 'To prod QA',
    'needs_decision': 'Park',
  };

  const label = (to) => {
    if (isRejection(to)) return 'Reject';
    // The two QA passes are not the same act and must not read as the same word. Passing on
    // test hands the task to production QA; passing on production finishes it, and nothing
    // follows that.
    if (to === 'ready_for_prod_qa' && task.lifecycle_status === 'ready_for_test_qa') return 'Pass';
    if (to === 'done') return compact ? 'Sign off' : 'Sign off — done';
    if (to === 'in_progress') {
      return task.lifecycle_status === 'needs_decision' ? 'Resume' : 'Start';
    }
    return (compact ? SHORT : LONG)[to] || LIFECYCLE_LABEL[to] || to;
  };

  // Exactly one filled button per task: the move that carries the work forward. Parking it and
  // sending it back are real options but not the expected one, and two ink-filled buttons side
  // by side is a choice presented as if it had no default.
  const isPrimary = (to) => !isRejection(to) && to !== 'needs_decision';

  const send = async (to, withNote = null) => {
    const ok = await onTransition(task, to, withNote);
    if (ok) { setRejecting(null); setNote(''); }
  };

  if (rejecting) {
    return (
      <div style={{
        display: 'flex', gap: 8, alignItems: 'flex-start',
        flexWrap: compact ? 'nowrap' : 'wrap',
      }}>
        <div style={{ minWidth: compact ? 120 : 220, flex: '1 1 auto' }}>
          <input
            className="o-control" autoFocus value={note}
            placeholder="What is still wrong?"
            onClick={e => e.stopPropagation()}
            onChange={e => setNote(e.target.value)}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Enter' && note.trim()) send(rejecting, note);
              if (e.key === 'Escape') { setRejecting(null); setNote(''); }
            }}
          />
          {/* Only where there is room. In a table row the placeholder is the instruction, and
              a second line of guidance would be the thing that made the row grow. */}
          {!compact && (
            <p className="o-note" style={{ marginTop: 6, marginBottom: 0 }}>
              Goes back to the developer with the task. It is all they get.
            </p>
          )}
        </div>
        <button className="o-btn o-btn-danger o-btn-sm" disabled={!note.trim() || saving}
          onClick={e => { e.stopPropagation(); send(rejecting, note); }}>
          {saving ? 'Sending…' : compact ? 'Send' : 'Send back'}
        </button>
        <button className="o-btn o-btn-ghost o-btn-sm"
          onClick={e => { e.stopPropagation(); setRejecting(null); setNote(''); }}>Cancel</button>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end',
      // Never wraps in a row: the row's height is the table's, not this control's.
      flexWrap: compact ? 'nowrap' : 'wrap',
    }}>
      {allowed.map(to => (
        <button
          key={to}
          className={`o-btn ${isPrimary(to) ? 'o-btn-primary' : 'o-btn-ghost'}${compact ? ' o-btn-sm' : ''}`}
          disabled={saving}
          title={`Move to ${LIFECYCLE_LABEL[to] || to}`}
          onClick={e => {
            e.stopPropagation();
            if (isRejection(to)) setRejecting(to);
            else send(to);
          }}
        >
          {label(to)}
        </button>
      ))}
    </div>
  );
}
